import React, { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  uploadQuickOrderFile,
  createQuickOrder,
} from "../../../redux/Thunk/QuickOrder/QuickOrderThunks";
import toast from "react-hot-toast";
import { paymentType, deliveryLocation } from "../../../utils/constants";

const QuickOrderOverlay = ({ isOpen, onClose, patient }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileType, setFileType] = useState(null); // 'image' | 'document'
  const [isUploading, setIsUploading] = useState(false);
  const [isPlacing, setIsPlacing] = useState(false);
  const [uploadedS3Url, setUploadedS3Url] = useState(null);
  const [qoNotes, setQoNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("upi");
  const [deliveryMethod, setDeliveryMethod] = useState("isClinic");
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 20;
    if (isAtBottom && showScrollIndicator) {
      setShowScrollIndicator(false);
    } else if (!isAtBottom && container.scrollHeight > container.clientHeight) {
      setShowScrollIndicator(true);
    }
  }, [showScrollIndicator]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const container = scrollContainerRef.current;
        if (container) {
          const hasScrollableContent = container.scrollHeight > container.clientHeight;
          const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 20;
          setShowScrollIndicator(hasScrollableContent && !isAtBottom);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowScrollIndicator(false);
    }
  }, [isOpen, uploadedFile]);

  // Disable body scroll when the overlay is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isOpen]);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isDoc =
      file.type === "application/pdf" ||
      file.type === "application/msword" ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

    if (!isImage && !isDoc) {
      toast.error("Please upload an image (JPG, PNG) or document (PDF, DOC)");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setUploadedFile(file);
    setFileType(isImage ? "image" : "document");
    setUploadedS3Url(null);

    if (isImage) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreviewUrl(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }
  }, []);

  const handleUploadToS3 = useCallback(async () => {
    if (!uploadedFile) return null;
    setIsUploading(true);
    try {
      const url = await dispatch(uploadQuickOrderFile(uploadedFile)).unwrap();
      setUploadedS3Url(url);
      setIsUploading(false);
      return url;
    } catch (err) {
      setIsUploading(false);
      return null;
    }
  }, [dispatch, uploadedFile]);

  const handleReset = useCallback(() => {
    setUploadedFile(null);
    setPreviewUrl(null);
    setFileType(null);
    setUploadedS3Url(null);
    setIsUploading(false);
    setIsPlacing(false);
    setQoNotes("");
    setPaymentMethod("upi");
    setDeliveryMethod("isClinic");
  }, []);

  const handlePlaceQuickOrder = useCallback(async () => {
    if (!uploadedFile && !uploadedS3Url) {
      toast.error("Please upload or capture a prescription image/PDF first.");
      return;
    }
    if (!patient?.phoneNumber) {
      toast.error("Patient phone number is required to place Quick Order.");
      return;
    }

    setIsPlacing(true);

    try {
      let finalS3Url = uploadedS3Url;
      if (!finalS3Url && uploadedFile) {
        finalS3Url = await handleUploadToS3(uploadedFile);
      }

      if (!finalS3Url) {
        toast.error("Failed to upload prescription. Please try again.");
        setIsPlacing(false);
        return;
      }

      const isClinic = deliveryMethod === "isClinic";
      const isCollect = deliveryMethod === "isCollect";
      const isAddress = deliveryMethod === "isAddress";

      const payload = {
        patient: {
          phoneNumber: patient.phoneNumber || "",
          name: patient.name || "",
          surname: patient.surname || "",
          gender: patient.gender || "",
          dateofbirth: patient.dateofbirth || "",
          patientAddress: patient.patientAddress || null,
        },
        filePath: finalS3Url,
        notes: qoNotes || "",
        payment: paymentMethod || "upi",
        isClinic,
        isCollect,
        isAddress,
      };

      const response = await dispatch(createQuickOrder(payload)).unwrap();
      if (response?.status === 200 || response?.status === 201) {
        toast.success("Quick Order placed successfully!");
        handleReset();
        onClose();
      } else {
        toast.error(response?.message || "Failed to place Quick Order");
      }
    } catch (err) {
      console.error("Place Quick Order error:", err);
    } finally {
      setIsPlacing(false);
    }
  }, [
    uploadedFile,
    uploadedS3Url,
    patient,
    dispatch,
    handleUploadToS3,
    onClose,
    qoNotes,
    paymentMethod,
    deliveryMethod,
    handleReset,
  ]);

  const handleReUpload = useCallback(() => {
    setUploadedFile(null);
    setPreviewUrl(null);
    setFileType(null);
    setUploadedS3Url(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  }, []);

  if (!isOpen) return null;

  const isUploadReady = !!uploadedFile;
  const isBusy = isUploading || isPlacing;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm transition-all p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200 relative">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-4 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-white font-bold text-lg leading-tight">
              Quick Order
            </h2>
            <p className="text-white/80 text-xs mt-0.5">
              Upload prescription to place order
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              handleReset();
              onClose();
            }}
            disabled={isBusy}
            className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors disabled:opacity-50"
          >
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Scrollable Content Container */}
        <div 
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200"
        >
          {/* Patient Info Badge */}
          <div className="px-5 pt-4 pb-2">
            <div className="bg-gray-50 rounded-xl px-3 py-2 border border-gray-100">
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1">
                Patient
              </p>
              <p className="text-sm font-semibold text-gray-800 leading-tight">
                {patient?.name || ""} {patient?.surname || ""}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {patient?.phoneNumber || ""}{" "}
                {patient?.gender ? `• ${patient.gender}` : ""}{" "}
                {patient?.age ? `• ${patient.age} yrs` : ""}
              </p>
            </div>
          </div>

          {/* Upload Area */}
          <div className="px-5 py-3">
            {!isUploadReady ? (
              /* Upload Buttons */
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Upload Prescription
                </p>

                <div className="grid grid-cols-2 gap-3">
                  {/* Camera Button */}
                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-amber-300 rounded-xl hover:border-amber-400 hover:bg-amber-50/50 transition-all group"
                  >
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                      <svg
                        className="w-5 h-5 text-amber-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </div>
                    <span className="text-xs font-bold text-gray-600">
                      Take Photo
                    </span>
                  </button>

                  {/* File Upload Button */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-blue-300 rounded-xl hover:border-blue-400 hover:bg-blue-50/50 transition-all group"
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                      <svg
                        className="w-5 h-5 text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                    </div>
                    <span className="text-xs font-bold text-gray-600">
                      Upload File
                    </span>
                  </button>
                </div>

                <p className="text-[10px] text-gray-400 text-center">
                  Supports JPG, PNG, PDF, DOC • Max 10MB
                </p>
              </div>
            ) : (
              /* Preview Area */
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Prescription Preview
                  </p>
                  <button
                    type="button"
                    onClick={handleReUpload}
                    disabled={isBusy}
                    className="text-xs font-bold text-amber-600 hover:text-amber-700 transition-colors disabled:opacity-50"
                  >
                    Re-upload
                  </button>
                </div>

                <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
                  {fileType === "image" && previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Prescription preview"
                      className="w-full max-h-[200px] object-contain bg-white"
                    />
                  ) : (
                    <div className="flex items-center gap-3 p-4">
                      <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                        <svg
                          className="w-5 h-5 text-red-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-700 truncate">
                          {uploadedFile?.name || "Document"}
                        </p>
                        <p className="text-xs text-gray-400">
                          {uploadedFile
                            ? `${(uploadedFile.size / 1024).toFixed(1)} KB`
                            : ""}
                        </p>
                      </div>
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                        <svg
                          className="w-3.5 h-3.5 text-green-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Order Notes Field */}
          <div className="px-5 py-2">
            <label htmlFor="qoNotes" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              Order Notes (Optional)
            </label>
            <textarea
              id="qoNotes"
              rows={3}
              value={qoNotes}
              onChange={(e) => setQoNotes(e.target.value)}
              disabled={isBusy}
              placeholder="Add optional notes about the order, products, quantity, dosage etc..."
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all resize-none placeholder:text-gray-400 text-gray-800"
            />
          </div>

          {/* Payment & Delivery Selection */}
          <div className="px-5 py-2 space-y-4 pb-6">
            {/* Payment Method */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Payment Method
              </label>
              <div className="grid grid-cols-3 gap-2">
                {paymentType?.map((option) => {
                  const isSelected = paymentMethod === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      disabled={isBusy}
                      onClick={() => setPaymentMethod(option.value)}
                      className={`py-2 px-3 rounded-xl border text-center font-bold text-xs transition-all flex items-center justify-center gap-1.5 active:scale-[0.98] ${
                        isSelected
                          ? "bg-amber-500 border-amber-600 text-white shadow-sm"
                          : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100/70"
                      }`}
                    >
                      {option.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Delivery Method */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Delivery Method
              </label>
              <div className="grid grid-cols-3 gap-2">
                {deliveryLocation?.map((option) => {
                  const isSelected = deliveryMethod === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      disabled={isBusy}
                      onClick={() => setDeliveryMethod(option.value)}
                      className={`py-2 px-3 rounded-xl border text-center font-bold text-xs transition-all flex items-center justify-center gap-1.5 active:scale-[0.98] ${
                        isSelected
                          ? "bg-orange-500 border-orange-600 text-white shadow-sm"
                          : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100/70"
                      }`}
                    >
                      {option.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Down Helper Pill */}
        {showScrollIndicator && (
          <div className="absolute bottom-[84px] left-1/2 -translate-x-1/2 z-[100] pointer-events-none animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[11px] font-extrabold px-3 py-1.5 rounded-full shadow-lg border border-orange-400/50 animate-bounce">
              <span className="tracking-wide">Scroll Down for Options</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" className="animate-pulse">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <polyline points="19 12 12 19 5 12"></polyline>
              </svg>
            </div>
          </div>
        )}

        {/* Action Button (Fixed Footer) */}
        <div className="px-5 pb-5 pt-3 border-t border-gray-100 shrink-0">
          <button
            type="button"
            onClick={handlePlaceQuickOrder}
            disabled={!isUploadReady || isBusy}
            className={`w-full py-3 rounded-xl font-bold text-sm transition-all shadow-lg active:scale-[0.98] ${
              isUploadReady && !isBusy
                ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 shadow-green-200"
                : "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
            }`}
          >
            {isBusy ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {isUploading ? "Uploading..." : "Placing Quick Order..."}
              </span>
            ) : (
              "Place Quick Order"
            )}
          </button>
        </div>
      </div>

      {/* Hidden File Inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

export default QuickOrderOverlay;
